import { LoaderFunctionArgs } from "react-router";
import { fetchJson } from "../../utils";
import { ProductType } from "../../types";
import HttpError from "../../utils/HttpError";

interface CacheEntry {
    data: ProductType;
    timestamp: number;
}

class ProductCache {
    private cache: Map<string, CacheEntry>;
    private readonly maxSize: number;
    private readonly ttl: number; // Time to live in milliseconds

    constructor(maxSize = 100, ttlMinutes = 5) {
        this.cache = new Map();
        this.maxSize = maxSize;
        this.ttl = ttlMinutes * 60 * 1000;
    }

    get(key: string): ProductType | null {
        const entry = this.cache.get(key);
        if (!entry) return null;

        const now = Date.now();
        if (now - entry.timestamp > this.ttl) {
            this.cache.delete(key);
            return null;
        }

        return entry.data;
    }

    set(key: string, value: ProductType): void {
        if (this.cache.size >= this.maxSize) {
            // Remove oldest entry
            const oldestKey = this.cache.keys().next().value;
            this.cache.delete(oldestKey);
        }

        this.cache.set(key, {
            data: value,
            timestamp: Date.now()
        });
    }

    clear(): void {
        this.cache.clear();
    }
}

// Create a singleton instance of the cache
const productCache = new ProductCache();

export const CardPageLoader = async ({ params }: LoaderFunctionArgs): Promise<ProductType | null> => {
    const slug = params.slug;

    if (!slug) {
        console.warn("No slug provided to CardPageLoader");
        return null;
    }

    // Try to get from cache first
    const cachedProduct = productCache.get(slug);
    if (cachedProduct) {
        return cachedProduct;
    }

    try {
        const { json, status } = await fetchJson<ProductType>(`/api/catalog/${slug}`);
        
        productCache.set(slug, json);
        return json;
    } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
            throw new Response(`Could not find product`, { status: error.status });
        }
        throw new Response(`HTTP error! status: ${status}`, { status });
    }
};
